import React, { useState } from 'react';
import {
  Stack,
  TextInput,
  Button,
  Group,
  Select,
  Textarea,
  NumberInput,
  Divider,
  Card,
  Text,
  ActionIcon,
  FileInput,
  Alert,
  JsonInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconPlus, IconTrash, IconAlertCircle, IconUpload, IconBrandYoutube // <-- Add this
} from '@tabler/icons-react';
import { classService } from '../../services/classService';

const ClassForm = ({ classItem, mode, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const form = useForm({
    initialValues: {
      title: classItem?.title || '',
      type: classItem?.type || 'video_class',
      short_description: classItem?.short_description || '',
      long_description: classItem?.long_description || '',
      vdocipher_video_id: classItem?.vdocipher_video_id || '',
      thumbnail_image: classItem?.thumbnail_image || '',
      live_link: classItem?.live_link || '',
      passkey: classItem?.passkey || '',
      quiz_json: classItem?.quiz_json || [],
      quiz_pass_rate: classItem?.quiz_pass_rate || 70,
      downloadable_material_links_json: classItem?.downloadable_material_links_json || [],
      hls_link: classItem?.hls_link || '',
      youtube_link: classItem?.youtube_link || '', // <-- Add this

    },
    validate: {
      title: (value) => (!value ? 'Title is required' : null),
      type: (value) => (!value ? 'Type is required' : null),
      short_description: (value) => (!value ? 'Short description is required' : null),
      vdocipher_video_id: (value, values) => {
        if (values.type === 'video_class' && !value) {
          return 'VdoCipher Video ID is required for video classes';
        }
        return null;
      },
      live_link: (value, values) => {
        if (values.type === 'live_class' && !value) {
          return 'Live link is required for live classes';
        }
        if (values.type === 'live_class' && value && !/^https?:\/\/.+/.test(value)) {
          return 'Please enter a valid URL';
        }
        return null;
      },
      quiz_pass_rate: (value, values) => {
        if (values.type === 'video_class' && values.quiz_json?.length > 0) {
          if (!value || value < 0 || value > 100) {
            return 'Quiz pass rate must be between 0 and 100';
          }
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const submitData = {
        ...values,
        youtube_link: values.youtube_link, // <-- Add this

        quiz_json: values.type === 'video_class' ? values.quiz_json : null,
        quiz_pass_rate: values.type === 'video_class' ? values.quiz_pass_rate : null,
        downloadable_material_links_json: values.type === 'video_class' ? values.downloadable_material_links_json : null,
        vdocipher_video_id: values.type === 'video_class' ? values.vdocipher_video_id : null,
        hls_link: values.type === 'video_class' ? values.hls_link : null,
        thumbnail_image: values.type === 'live_class' ? values.thumbnail_image : null,
        live_link: values.type === 'live_class' ? values.live_link : null,
        passkey: values.type === 'live_class' ? values.passkey : null,
      };

      if (mode === 'create') {
        await classService.createClass(submitData);
        notifications.show({
          title: 'Success',
          message: 'Class created successfully',
          color: 'green',
        });
      } else {
        await classService.updateClass(classItem.id, submitData);
        notifications.show({
          title: 'Success',
          message: 'Class updated successfully',
          color: 'green',
        });
      }
      onSubmit();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save class';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploadingFile(true);
    try {
      const uploadResult = await classService.uploadFile(file);

      const newMaterial = {
        url: uploadResult.url,
        filename: uploadResult.filename,
        size: uploadResult.size,
      };

      const currentMaterials = form.values.downloadable_material_links_json || [];
      form.setFieldValue('downloadable_material_links_json', [...currentMaterials, newMaterial]);

      notifications.show({
        title: 'Success',
        message: 'File uploaded successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload file',
        color: 'red',
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const removeMaterial = (index) => {
    const materials = form.values.downloadable_material_links_json || [];
    materials.splice(index, 1);
    form.setFieldValue('downloadable_material_links_json', [...materials]);
  };

  const addQuizQuestion = () => {
    const currentQuiz = form.values.quiz_json || [];
    const newQuestion = {
      question: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      explanation: ''
    };
    form.setFieldValue('quiz_json', [...currentQuiz, newQuestion]);
  };

  const removeQuizQuestion = (index) => {
    const quiz = form.values.quiz_json || [];
    quiz.splice(index, 1);
    form.setFieldValue('quiz_json', [...quiz]);
  };

  const updateQuizQuestion = (index, field, value) => {
    const quiz = [...(form.values.quiz_json || [])];
    if (field === 'options') {
      quiz[index] = { ...quiz[index], options: value };
    } else {
      quiz[index] = { ...quiz[index], [field]: value };
    }
    form.setFieldValue('quiz_json', quiz);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {mode === 'edit' && classItem && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            Editing class: {classItem.title}
          </Alert>
        )}

        <TextInput
          label="Class Title"
          placeholder="Enter class title"
          required
          {...form.getInputProps('title')}
        />

        <Select
          label="Class Type"
          data={[
            { value: 'video_class', label: 'Video Class' },
            { value: 'live_class', label: 'Live Class' }
          ]}
          required
          {...form.getInputProps('type')}
        />

        <Textarea
          label="Short Description"
          placeholder="Brief description of the class"
          required
          rows={3}
          {...form.getInputProps('short_description')}
        />

        <Textarea
          label="Long Description (Optional)"
          placeholder="Detailed description of the class"
          rows={4}
          {...form.getInputProps('long_description')}
        />
        {/* ADD THIS NEW INPUT */}
        <TextInput
          label="Optional YouTube Link"
          placeholder="e.g., https://www.youtube.com/watch?v=..."
          {...form.getInputProps('youtube_link')}
        />
        <Divider label={`${form.values.type === 'video_class' ? 'Video' : 'Live'} Class Settings`} />

        {form.values.type === 'video_class' ? (
          <Stack>
            <TextInput
              label="Bunny Stream Video ID"
              placeholder="Enter Bunny Stream video ID (e.g., df488852-6002-4594...)"
              required
              {...form.getInputProps('vdocipher_video_id')}
            />
            <TextInput
              label="HLS Stream Link (Optional)"
              placeholder="Enter HLS stream URL"
              {...form.getInputProps('hls_link')}
            />
            <Divider label="Quiz Configuration" />

            <Group justify="space-between">
              <Text fw={600}>Quiz Questions</Text>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addQuizQuestion}
              >
                Add Question
              </Button>
            </Group>

            {form.values.quiz_json?.map((question, index) => (
              <Card key={index} withBorder p="md">
                <Stack>
                  <Group justify="space-between">
                    <Text fw={600}>Question {index + 1}</Text>
                    <ActionIcon
                      color="red"
                      variant="light"
                      onClick={() => removeQuizQuestion(index)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>

                  <TextInput
                    label="Question"
                    placeholder="Enter question"
                    value={question.question}
                    onChange={(e) => updateQuizQuestion(index, 'question', e.target.value)}
                  />

                  <Text size="sm" fw={500}>Options</Text>
                  {question.options?.map((option, optIndex) => (
                    <TextInput
                      key={optIndex}
                      placeholder={`Option ${optIndex + 1}`}
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...question.options];
                        newOptions[optIndex] = e.target.value;
                        updateQuizQuestion(index, 'options', newOptions);
                      }}
                    />
                  ))}

                  <Select
                    label="Correct Answer"
                    data={question.options?.map((option, optIndex) => ({
                      value: optIndex.toString(),
                      label: `Option ${optIndex + 1}: ${option || 'Empty'}`
                    })) || []}
                    value={question.correct_answer?.toString()}
                    onChange={(value) => updateQuizQuestion(index, 'correct_answer', parseInt(value))}
                  />

                  <Textarea
                    label="Explanation (Optional)"
                    placeholder="Explain why this is the correct answer"
                    value={question.explanation || ''}
                    onChange={(e) => updateQuizQuestion(index, 'explanation', e.target.value)}
                    rows={2}
                  />
                </Stack>
              </Card>
            ))}

            {form.values.quiz_json?.length > 0 && (
              <NumberInput
                label="Quiz Pass Rate (%)"
                placeholder="Minimum percentage to pass"
                min={0}
                max={100}
                {...form.getInputProps('quiz_pass_rate')}
              />
            )}

            <Divider label="Downloadable Materials" />

            <Group justify="space-between">
              <Text fw={600}>Materials</Text>
              <FileInput
                placeholder="Upload file"
                leftSection={<IconUpload size={16} />}
                onChange={handleFileUpload}
                loading={uploadingFile}
                accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar"
              />
            </Group>

            {form.values.downloadable_material_links_json?.map((material, index) => (
              <Card key={index} withBorder p="sm">
                <Group justify="space-between">
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>{material.filename}</Text>
                    <Text size="xs" c="dimmed">
                      Size: {(material.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Stack>
                  <ActionIcon
                    color="red"
                    variant="light"
                    onClick={() => removeMaterial(index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Card>
            ))}
          </Stack>
        ) : (
          <Stack>
            <TextInput
              label="Live Stream Link"
              placeholder="Enter live stream URL"
              required
              {...form.getInputProps('live_link')}
            />

            <TextInput
              label="Thumbnail Image URL (Optional)"
              placeholder="Enter thumbnail image URL"
              {...form.getInputProps('thumbnail_image')}
            />

            <TextInput
              label="Passkey (Optional)"
              placeholder="Enter access passkey if required"
              {...form.getInputProps('passkey')}
            />
          </Stack>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            {mode === 'create' ? 'Create Class' : 'Update Class'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default ClassForm;